// Runtime diagnostics publisher for debug/map/performance/observed outputs.
// It keeps side-effectful logging and debug-map publishing out of the compile core flow.
import type { LuisCompileDebugRule, LuisCompileWarning } from '@lentystyle/compiler'
import type {
  LuisDebugFeatureApi,
  LuisLazyFeatureApi,
  LuisMapFeatureApi,
  LuisObservedFeatureApi,
  LuisPerformanceFeatureApi,
  LuisRuntimeTimingBreakdown,
} from '../../features'
import type { BuildSourceEntry } from './types'
import { buildObservedDebugRules } from './css-compose'
import { resolveSourceLabel } from './reporting'
import type { LuisDispatchedCompileResult } from '../../compile-dispatch/dispatch'

type PublishRuntimeDiagnosticsOptions = {
  compile: LuisDispatchedCompileResult
  timing: LuisRuntimeTimingBreakdown
  debugEnabled: boolean
  mapEnabled: boolean
  performanceEnabled: boolean
  lazyEnabled: boolean
  debugFeature?: LuisDebugFeatureApi
  mapFeature?: LuisMapFeatureApi
  lazyFeature?: LuisLazyFeatureApi
  performanceFeature?: LuisPerformanceFeatureApi
  observedFeature?: LuisObservedFeatureApi
}

// Publish debug maps, observed rules, and perf logs for one compiled source.
export function publishRuntimeDiagnostics(
  entry: BuildSourceEntry,
  compileSourceId: string,
  compileWarnings: LuisCompileWarning[],
  remappedDebugRules: LuisCompileDebugRule[] | undefined,
  cssLength: number,
  options: PublishRuntimeDiagnosticsOptions,
): void {
  const sourceLabel = resolveSourceLabel(entry)
  const compileDebugEnabled = options.debugEnabled || options.mapEnabled

  // Emit debug logs only when debug feature is active.
  if (options.debugEnabled && options.debugFeature) {
    options.debugFeature.logCompileWarnings(compileWarnings, sourceLabel)
    options.debugFeature.logLoadedLuisImports(entry, sourceLabel)
    options.debugFeature.logImportedLuisDiagnostics(sourceLabel, entry.importMetrics)
  } else {
    // Skip debug logs when debug mode is disabled.
  }

  // Publish rule map when debug/map metadata is active.
  if (compileDebugEnabled && options.debugFeature) {
    options.debugFeature.publishDebugRuleMap(
      entry,
      sourceLabel,
      remappedDebugRules,
      compileDebugEnabled,
      options.debugEnabled,
    )
  } else {
    options.debugFeature?.clearDebugRuleMap(entry.styleTag.id)
  }

  // Upsert observed selector output in separate style tag when rules exist.
  const observedRules = options.compile.result.observedRules ?? []
  const initialObservedRules = options.compile.result.initialObservedRules ?? []
  const observedRuntimeRules = options.lazyEnabled ? [...observedRules, ...initialObservedRules] : observedRules
  if (options.observedFeature && observedRuntimeRules.length > 0) {
    options.observedFeature.upsertObservedRules({
      hostStyleTag: entry.styleTag,
      sourceId: compileSourceId,
      sourceLabel,
      sourceContent: entry.rawSource,
      rules: observedRules,
      initialRules: options.lazyEnabled ? initialObservedRules : [],
      debugRules: buildObservedDebugRules(remappedDebugRules, observedRuntimeRules),
      debugFeature: options.debugFeature,
      mapFeature: options.mapFeature,
      lazyFeature: options.lazyFeature,
      mapEnabled: options.mapEnabled,
      debugEnabled: options.debugEnabled,
      performanceEnabled: options.performanceEnabled,
      lazyEnabled: options.lazyEnabled,
      performanceFeature: options.performanceFeature,
    })
  } else {
    if (options.observedFeature) {
      options.observedFeature.removeObservedRules(entry.styleTag.id)
    } else {
      // Remove stale observed style tag even when plugin bundle is not loaded.
      document.getElementById(`${entry.styleTag.id}-observed`)?.remove()
    }
  }

  // Log compile timing when performance flag is enabled.
  if (options.performanceEnabled && options.performanceFeature) {
    options.performanceFeature.logCompilePerformance({
      sourceLabel,
      cssLength,
      mode: options.compile.incremental?.mode,
      execution: options.compile.execution,
      workerState: options.compile.workerState,
      incremental: options.compile.incremental,
      timing: options.timing,
    })
    logImportedPerformanceMetrics(options.performanceFeature, sourceLabel, entry)
  } else {
    // Skip perf logging when performance flag is not enabled.
  }
}

// Emit per-import performance lines for resolved runtime `.luis` imports.
function logImportedPerformanceMetrics(
  performanceFeature: LuisPerformanceFeatureApi,
  sourceLabel: string,
  entry: BuildSourceEntry,
): void {
  // Skip import performance logging when source has no import metrics.
  if (entry.importMetrics.length === 0) {
    return
  } else {
    // Continue one-line perf logging for each import metric.
  }

  // Emit compile-like timing log for each resolved import.
  for (const metric of entry.importMetrics) {
    performanceFeature.logImportPerformance({
      sourceLabel,
      importHref: metric.href,
      importerSourceId: metric.importerSourceId,
      compileDurationMs: metric.fetchDurationMs,
      cssLength: metric.sourceLength,
      fromCache: metric.fromCache,
    })
  }
}
